import * as React from 'react';
import { resolve } from 'inversify-react';
import { observer } from 'mobx-react';
import { Button, Modal, Icon } from 'antd';
import autobind from 'autobind-decorator';

import { Column, DataTableView, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { FormatService } from 'src/core/services/format.service';
import { CardController } from '../../../shared/card.controller';
import { PaymentModal, TargetModel } from 'src/app/bookings/quotes/parts/payment-modal';
import { TooltipView } from 'src/core/ui/tooltip.view';
import { ObjectUtils } from 'src/core/utils/object';

interface Props extends ComponentViewProps<DataSet<PaymentSummary>> {
	type: 'agent' | 'client';
	targetOptions: TargetModel[];
	permissions: QuotePaymentPermission[];
	mode: ControlMode;
	defaultCurrencyId: number;
	defaultCurrencyCode: string;
	quote: QuoteItem;
	onPerformPayment: (payment: PaymentSummary) => void;
	onSubmitPayment: (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean) => Promise<void>;
	onSubmitRefund: (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean, targetPaymentId?: number) => Promise<void>;
}

interface State {
	confirmationDialogData: {
		title: string;
		message: React.ReactNode;
		onOk: () => void;
	};
	paymentDialogData: {
		props: {
			title: string;
			amount?: number;
			minAmount?: number;
			maxAmount?: number;
			transactionId?: string;
			disableCreditCardEntry?: boolean;
			onSubmitPayment: (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean) => Promise<void>;
			onCancelDialog: () => void;
			target: {
				options: TargetModel[];
				render?: (selected: TargetModel) => React.ReactNode;
			}
		}
		body: React.ReactNode;
	};
}

export type QuotePaymentPermission = 'register-payment' | 'register-refund' | 'perform-payment';

@observer
export class QuotePaymentList extends React.Component<Props, State> {
	@resolve
	private formatService: FormatService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			confirmationDialogData: null,
			paymentDialogData: null
		};
	}

	private rowKey = (record: PaymentSummary): string => record.id.toString();

	private renderItemsPerLine(items: any[]): React.ReactNode {
		return items.map((m, index) => (
			<React.Fragment key={index}>
				{index > 0 && <br />}
				{m}
			</React.Fragment>
		));
	}

	@autobind
	private handleOkConfirmationDialog(): void {
		const { confirmationDialogData } = this.state;
		confirmationDialogData.onOk();
		this.handleCloseConfirmationDialog();
	}

	@autobind
	private handleCloseConfirmationDialog(): void {
		this.setState({ confirmationDialogData: null });
	}

	@autobind
	private handlePerformPayment(payment: PaymentSummary): void {
		const { onPerformPayment } = this.props;
		this.setState({
			confirmationDialogData: {
				title: 'Perform Payment',
				message: (
					<React.Fragment>
						<h4>Are you sure you want to perform this payment?</h4>
					</React.Fragment>
				),
				onOk: () => onPerformPayment(payment)
			}
		});
	}

	@autobind
	private handleRegisterPayment(): void {
		const { onSubmitPayment, targetOptions, quote } = this.props;

		const amount = ObjectUtils.round(quote.totalSaleGross - quote.paid, 2);
		this.setState({
			paymentDialogData: {
				props: {
					title: 'Register Payment',
					subtitle: 'Make a payment',
					amount: amount,
					minAmount: 1,
					maxAmount: amount,
					onSubmitPayment: async (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean) => {
						this.setState({ paymentDialogData: null });
						await onSubmitPayment(paymentInfo, immediatelyPerformPayment);
					},
					onCancelDialog: () => {
						this.setState({ paymentDialogData: null });
					},
					target: {
						options: targetOptions
					}
				},
				body: null
			}
		});
	}

	@autobind
	private handleRegisterRefund(payment?: PaymentSummary): void {
		const { onSubmitRefund, quote, targetOptions, type, defaultCurrencyCode } = this.props;

		let amount = 0;
		let maxAmount = quote.paid;
		let transactionId = null;
		let targetPaymentId = null;
		let options = targetOptions;
		const fieldId = type === 'client' ? 'clientId' : 'agentId';
		const fieldDisplayName = type === 'client' ? 'clientDisplayName' : 'agentName';
		const displayName = payment[fieldDisplayName].split(" ");

		if (payment != null) {
			amount = Math.min(quote.paid, ObjectUtils.round(payment.sourceAmount))
			maxAmount = amount;
			transactionId = payment.transactionId;
			targetPaymentId = payment.id;
			options = [{
				id: payment[fieldId],
				firstName: displayName[0],
				lastName: displayName.filter((e, i) => i > 0).join(" "),
				displayName: payment[fieldDisplayName],
			}];
		}

		this.setState({
			paymentDialogData: {
				props: {
					title: 'Register Refund',
					subtitle: 'Refund payment',
					amount: amount,
					maxAmount: maxAmount,
					transactionId: transactionId,
					disableCreditCardEntry: !transactionId,
					onSubmitPayment: async (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean) => {
						this.setState({ paymentDialogData: null });
						await onSubmitRefund(paymentInfo, immediatelyPerformPayment, targetPaymentId);
					},
					onCancelDialog: () => {
						this.setState({ paymentDialogData: null });
					},
					target: {
						options: options
					}
				},
				body:
					<div className="display-field">
						{payment != null
							? (
								<React.Fragment>
									<label>{payment[fieldDisplayName]} - {payment.methodText} ({this.formatService.toString('currency', payment.amount, payment.currencyFormat)}):</label>
									{
										payment.paymentReference &&
										<React.Fragment>
											<b>PaymentReference:</b> {payment.paymentReference} <br />
										</React.Fragment>
									}
									<b>Currency:</b> {payment.currencyIsoCode} <br />
									<b>Amount in Source Currency:</b> {ObjectUtils.round(payment.sourceAmount)} {defaultCurrencyCode} <br />
									<b>Payment Date:</b> {this.formatService.toString('date', payment.paymentDate, 'utc')} <br />
								</React.Fragment>
							)
							: (
								<React.Fragment>
									<b>Max Amount:</b> {maxAmount} {defaultCurrencyCode}
								</React.Fragment>
							)
						}
					</div>
			}
		});
	}
	
	render(): React.ReactNode {
		const { type, permissions, model, mode, defaultCurrencyId, defaultCurrencyCode, quote, setBusy } = this.props;
		if (model == null) {
			return null;
		}
		const { confirmationDialogData, paymentDialogData } = this.state;

		let title = 'Client Payments';
		let accountingTarget = 1;
		let titleColumn = 'Client';
		let fieldNameColumn = 'clientDisplayName';
		if (type === 'agent') {
			title = 'Agent Payments';
			accountingTarget = 2;
			titleColumn = 'Agent';
			fieldNameColumn = 'agentName';
		}

		const columns: Column[] = [
			{
				title: 'Status',
				key: 'statusText',
				dataIndex: 'statusText',
				width: 200,
				render: (_, record: PaymentSummary) => {
					let className = 'btn-white';
					let icon = 'play-circle';
					switch (record.status) {
						case 1:
							className = 'btn-blue';
							icon = 'play-circle';
							break;
						case 4:
							className = 'btn-blue';
							icon = 'loading';
							break;
						case 2:
							className = 'btn-green';
							icon = 'check';
							break;
						case 3:
						case 5:
							className = 'btn-red';
							icon = 'reload';
							break;
						case 6:
							className = 'btn-black';
							icon = 'stop';
							break;
					}

					const isCompleted = record.status === 2 || record.status === 6;
					const isFailedCreditCard = record.method === 2 && (record.status === 3 || record.transactionId == null);

					return (
						<Button.Group>
							<Button
								disabled
								className={className}
								style={{ width: 100 }}>
								{record.statusText}
							</Button>
							<Button
								disabled={isCompleted || isFailedCreditCard || permissions.every(m => m !== 'perform-payment')}
								icon={icon}
								onClick={() => this.handlePerformPayment(record)}
							/>
						</Button.Group>
					);
				}
			},
			{
				title: 'Payment Number',
				key: 'paymentNumber',
				dataIndex: 'paymentNumber',
				width: 100,
			},
			{
				title: titleColumn,
				key: fieldNameColumn,
				dataIndex: fieldNameColumn,
				width: 150,
			},
			{
				title: 'Type',
				key: 'typeText',
				dataIndex: 'typeText',
				width: 150
			},
			{
				title: 'Method',
				key: 'methodText',
				dataIndex: 'methodText',
				width: 100
			},
			{
				title: 'Payment Date',
				key: 'paymentDate',
				dataIndex: 'paymentDate',
				width: 200,
				render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
			},
			{
				title: 'Currency',
				key: 'currencyIsoCode',
				dataIndex: 'currencyIsoCode',
				width: 100
			},
			{
				title: 'Amount',
				key: 'amount',
				dataIndex: 'amount',
				width: 100,
				render: (field: number, record: PaymentSummary) => {
					let formattedAmount: React.ReactNode = this.formatService.toString('currency', field, record.currencyFormat);
					if (record.status === 2) {
						formattedAmount = (
							<label style={{ color: record.type === 0 ? 'green' : 'red' }}>
								{formattedAmount}
							</label>
						);
					}
					if (record.currencyRate !== 1) {
						formattedAmount = (
							<TooltipView title={`${ObjectUtils.round(record.sourceAmount)} ${defaultCurrencyCode}`}>
								<span style={{ marginRight: '3px' }}>{formattedAmount}</span> <Icon type="exclamation-circle" />
							</TooltipView>
						);
					}

					return formattedAmount;
				}
			},
			{
				title: 'Payment Reference',
				key: 'paymentReference',
				dataIndex: 'paymentReference',
				width: 100
			},
			{
				title: 'Description',
				key: 'description',
				dataIndex: 'description',
				width: 150
			}
		];

		if (permissions.some(m => m === 'register-refund')) {
			columns.push({
				title: '',
				key: 'commands',
				width: 200,
				render: (_, record: PaymentSummary) => (
					<Button
						disabled={record.status !== 2 || record.type === 1 || mode === 'display' || quote.paid === 0}
						icon="credit-card"
						className="btn-red-icon"
						onClick={() => this.handleRegisterRefund(record)}>
						Refund
					</Button>
				)
			});
		}

		return (
			<React.Fragment>
				{
					confirmationDialogData &&
					<Modal
						visible
						maskClosable
						wrapClassName="vertical-center-modal"
						title={confirmationDialogData.title}
						width={480}
						onOk={this.handleOkConfirmationDialog}
						onCancel={this.handleCloseConfirmationDialog}
						footer={[
							<Button
								key="ok"
								type="primary"
								onClick={this.handleOkConfirmationDialog}>
								Ok
							</Button>,
							<Button
								key="cancel"
								onClick={this.handleCloseConfirmationDialog}>
								Cancel
							</Button>
						]}>
						{confirmationDialogData.message}
					</Modal>
				}
				{
					paymentDialogData &&
					<PaymentModal
						editableAmount
						quoteId={quote.id}
						defaultCurrencyId={defaultCurrencyId}
						defaultCurrencyCode={defaultCurrencyCode}
						bookingDate={quote.reservationDate}
						serviceDate={quote.dateFrom}
						accountingTarget={accountingTarget}
						setBusy={setBusy}
						{...paymentDialogData.props}>
						{
							paymentDialogData.body
						}
					</PaymentModal>
				}
				<CardController
					title={title}
					icon="pound"
					className="card-borderless"
					mode="edit">
					{
						permissions.some(m => m === 'register-payment') &&
						<Button.Group style={{ margin: "4px 2px", textAlign: 'right' }}>
							<Button
								disabled={mode === 'display'}
								icon="credit-card"
								className="btn-green-icon"
								onClick={() => this.handleRegisterPayment()}>
								Register Payment
							</Button>
						</Button.Group>
					}
					{
						permissions.some(m => m === 'register-refund') &&
						<Button.Group style={{ margin: "4px 2px", textAlign: 'right' }}>
							<Button
								disabled={mode === 'display' || quote.paid === 0}
								icon="credit-card"
								className="btn-red-icon"
								onClick={() => this.handleRegisterRefund()}>
								Register Refund
							</Button>
						</Button.Group>
					}
					<DataTableView
						mode="edit"
						rowKey={this.rowKey}
						model={model}
						columns={columns}
						setBusy={setBusy}
						hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
						selection={false}
					/>
				</CardController>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/shared/payments/quote-payment-list.tsx